'use client';

/**
 * Browser-side admin session.
 *
 * Tokens live in localStorage, which is the usual trade-off for a Bearer-token
 * SPA: it survives reloads and new tabs, at the cost of being readable by any
 * script on the page. Access tokens are short-lived to limit that exposure.
 * Moving to httpOnly cookies later means changing only this file plus the two
 * auth routes.
 */

const ACCESS_KEY = 'kl-meet.access';
const REFRESH_KEY = 'kl-meet.refresh';

export function getAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(ACCESS_KEY);
}

export function getRefreshToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(REFRESH_KEY);
}

export function storeTokens(access: string, refresh: string): void {
  window.localStorage.setItem(ACCESS_KEY, access);
  window.localStorage.setItem(REFRESH_KEY, refresh);
}

export function clearTokens(): void {
  window.localStorage.removeItem(ACCESS_KEY);
  window.localStorage.removeItem(REFRESH_KEY);
}

/** Exchanges credentials for a token pair and stores them. */
export async function login(username: string, password: string): Promise<void> {
  const res = await fetch('/api/auth/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Login failed');
  }

  storeTokens(data.access, data.refresh);
}

/**
 * Ends the session server-side, then clears local storage.
 *
 * Clearing localStorage alone left the refresh token valid for its remaining
 * seven days, so anything that had copied it stayed logged in. Local tokens are
 * cleared even if the request fails — the user asked to be logged out, and the
 * session expires on its own regardless.
 *
 * Pass `{ all: true }` to end every session for the account, not just this one.
 */
export async function logout(options: { all?: boolean } = {}): Promise<void> {
  const refresh = getRefreshToken();

  try {
    if (refresh) {
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh, all: options.all === true }),
      });
    }
  } catch {
    // Offline, or the server is down. Falling through to clearTokens() still
    // logs this browser out.
  } finally {
    clearTokens();
  }
}

/** Trades the stored refresh token for a new access token. */
async function refreshAccessToken(): Promise<string | null> {
  const refresh = getRefreshToken();
  if (!refresh) return null;

  try {
    const res = await fetch('/api/auth/token/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh }),
    });

    if (!res.ok) {
      clearTokens();
      return null;
    }

    const data = await res.json().catch(() => ({}));
    if (typeof data.access !== 'string') {
      clearTokens();
      return null;
    }

    window.localStorage.setItem(ACCESS_KEY, data.access);
    return data.access;
  } catch (err) {
    console.warn('Network error during token refresh:', err);
    return null;
  }
}

/**
 * fetch() with the admin access token attached.
 *
 * On a 401 it silently refreshes once and retries. Access tokens expire after
 * 15 minutes, so without this an admin would be bounced to /login mid-session
 * while their refresh token is still perfectly good.
 */
export async function authFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const withAuth = (token: string): RequestInit => ({
    ...init,
    headers: {
      ...(init.headers || {}),
      Authorization: `Bearer ${token}`,
    },
  });

  const access = getAccessToken();
  if (!access) {
    return new Response(JSON.stringify({ error: 'Not authenticated' }), {
      status: 401,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  try {
    const res = await fetch(input, withAuth(access));
    if (res.status !== 401) return res;

    const refreshed = await refreshAccessToken();
    if (!refreshed) return res;

    return await fetch(input, withAuth(refreshed));
  } catch (err) {
    console.warn('Network error during authFetch:', err);
    return new Response(JSON.stringify({ error: 'Network error or server unavailable' }), {
      status: 503,
      headers: { 'Content-Type': 'application/json' },
    });
  }
}

/** True if the stored access token is currently accepted by the server. */
export async function isAuthenticated(): Promise<boolean> {
  if (!getAccessToken()) return false;
  try {
    const res = await authFetch('/api/auth/me');
    return res.ok;
  } catch {
    return false;
  }
}
